import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

export interface ModalShellProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  maxWidth?: string;
  maxHeight?: string;
  scrollable?: boolean;
  contentOverflow?: "visible" | "auto" | "hidden";
  shrink?: boolean;
  minWidth?: string;
  footer?: React.ReactNode;
  ariaLabel?: string;
  contentClassName?: string;
  showCloseButton?: boolean;
  closeOnEscape?: boolean;
}

let openModalsCount = 0;
let bodyOverflowBefore = "";
const openModalStack: string[] = [];
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const getFocusableElements = (container: HTMLElement) =>
  Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (element) =>
      !element.hidden &&
      element.getAttribute("aria-hidden") !== "true" &&
      element.offsetParent !== null,
  );

export const ModalShell = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  className = "",
  maxWidth = "max-w-2xl",
  maxHeight = "max-h-[80dvh]",
  scrollable = false,
  contentOverflow,
  shrink = false,
  minWidth,
  footer,
  ariaLabel,
  contentClassName = "",
  showCloseButton = true,
  closeOnEscape = true,
}: ModalShellProps) => {
  const modalId = useId();
  const titleId = `${modalId}-title`;
  const subtitleId = `${modalId}-description`;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousActive = useRef<HTMLElement | null>(null);
  const latestOnClose = useRef(onClose);
  const { t } = useTranslation("common");

  useEffect(() => {
    latestOnClose.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    previousActive.current = document.activeElement as HTMLElement | null;
    openModalStack.push(modalId);

    const onKey = (event: KeyboardEvent) => {
      if (openModalStack[openModalStack.length - 1] !== modalId) {
        return;
      }

      if (event.key === "Escape" && closeOnEscape) {
        event.preventDefault();
        latestOnClose.current();
        return;
      }

      if (event.key !== "Tab" || !panelRef.current) {
        return;
      }

      const focusableElements = getFocusableElements(panelRef.current);
      if (focusableElements.length === 0) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    const focusFrame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const autofocusTarget = panel.querySelector<HTMLElement>("[autofocus]");
      (autofocusTarget ?? getFocusableElements(panel)[0] ?? panel).focus();
    });

    if (openModalsCount === 0) {
      bodyOverflowBefore = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }
    openModalsCount += 1;

    return () => {
      document.removeEventListener("keydown", onKey);
      window.cancelAnimationFrame(focusFrame);
      const stackIndex = openModalStack.lastIndexOf(modalId);
      if (stackIndex >= 0) {
        openModalStack.splice(stackIndex, 1);
      }
      openModalsCount = Math.max(0, openModalsCount - 1);
      if (openModalsCount === 0) {
        document.body.style.overflow = bodyOverflowBefore;
      }
      try {
        previousActive.current?.focus();
      } catch (error) {
        void error;
      }
    };
  }, [closeOnEscape, modalId, open]);

  if (!open) return null;

  const containerClass = `flex max-h-[90dvh] w-full flex-col ${maxWidth} ${
    shrink ? (minWidth ?? "") : ""
  } rounded-floating border border-border-subtle bg-surface-primary px-surface-comfortable-x py-surface-comfortable-y shadow-floating ${className}`;
  const resolvedContentOverflow =
    contentOverflow ?? (scrollable ? "auto" : "visible");
  const overflowClass = {
    visible: "overflow-visible",
    auto: "overflow-auto",
    hidden: "overflow-hidden",
  }[resolvedContentOverflow];
  const contentClass = `mt-4 min-h-0 flex-1 space-y-3 ${maxHeight} ${overflowClass} ${contentClassName}`;

  return createPortal(
    <div className="fixed inset-0 z-50 isolate flex items-center justify-center bg-surface-accent/35 px-4">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={title && !ariaLabel ? titleId : undefined}
        aria-describedby={subtitle ? subtitleId : undefined}
        tabIndex={-1}
        className={containerClass}
      >
        <header className="flex shrink-0 items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            {subtitle && (
              <p id={subtitleId} className="text-body text-content-muted">
                {subtitle}
              </p>
            )}
            {title && (
              <h2
                id={titleId}
                className="break-words font-display text-heading-lg font-semibold text-content-primary"
              >
                {title}
              </h2>
            )}
          </div>
          {showCloseButton && (
            <button
              type="button"
              className="min-h-control-sm shrink-0 rounded-action px-2 text-body text-content-muted transition-colors duration-fast ease-standard hover:bg-surface-secondary hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-primary"
              onClick={onClose}
              aria-label={t("common.aria.closeModal")}
            >
              {t("common:actions.close")}
            </button>
          )}
        </header>

        <div className={contentClass}>{children}</div>

        {footer && (
          <footer className="mt-4 shrink-0 border-t border-border-subtle pt-4">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default ModalShell;
